import { format } from 'date-fns';
import { AlertTriangle, Clock, CheckCircle, XCircle, Eye, MapPin, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FraudReport } from '@/lib/api';
import { fraudCategories, communicationMediums } from '@/data/bangladesh';

interface FraudCardProps {
  report: FraudReport;
  onViewDetails: (report: FraudReport) => void;
  showPhoneNumber?: boolean;
}

export function FraudCard({ report, onViewDetails, showPhoneNumber = true }: FraudCardProps) {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'approved':
        return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500', label: 'Verified' };
      case 'rejected':
        return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500', label: 'Rejected' };
      default:
        return { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500', label: 'Pending' };
    }
  };

  const getCategoryLabel = (category: string) => {
    return fraudCategories.find(cat => cat.value === category)?.label || category;
  };

  const getMediumLabel = (medium: string) => {
    return communicationMediums.find(med => med.value === medium)?.label || medium;
  };

  const statusConfig = getStatusConfig(report.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="relative flex gap-4">
      {/* Timeline line and dot */}
      <div className="flex flex-col items-center">
        <div className={`w-4 h-4 rounded-full ${statusConfig.bg} ring-4 ring-white shadow-md flex-shrink-0`} />
        <div className="w-0.5 bg-slate-200 flex-grow mt-2" />
      </div>

      {/* Card content */}
      <div className="flex-1 pb-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 text-base leading-tight mb-2">
                  {report.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-slate-800 hover:bg-slate-700 text-white text-xs">
                    {getCategoryLabel(report.fraud_category)}
                  </Badge>
                  <Badge variant="outline" className="text-xs border-slate-300 text-slate-600">
                    {getMediumLabel(report.medium)}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <StatusIcon className={`h-5 w-5 ${statusConfig.color}`} />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 space-y-3">
            <p className="text-sm text-slate-700 leading-relaxed line-clamp-2">
              {report.description}
            </p>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {report.district}, {report.division}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {format(new Date(report.created_at), 'MMM dd, yyyy')}
              </span>
            </div>

            {/* Amount if present */}
            {report.amount_involved && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-2 rounded-lg">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="font-semibold">Amount: ৳{report.amount_involved.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 pb-4">
            <Button 
              onClick={() => onViewDetails(report)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              size="sm"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
